// ============================================================
// GitSubmodules.jsx
//
// A branded, responsive course content page for the
// "Git Submodules" lesson.
//
// Author: ProDev Enterprise Series (DevOps Branch)
// Features: Parent-child repository relationships, submodule
//           initialization, and version locking logic.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import ExtensionIcon          from "@mui/icons-material/Extension";
import LibraryAddIcon         from "@mui/icons-material/LibraryAdd";
import SyncIcon               from "@mui/icons-material/Sync";
import DeleteSweepIcon        from "@mui/icons-material/DeleteSweep";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import HubIcon                from "@mui/icons-material/Hub";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function GitSubmodules() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<ExtensionIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Advanced Repository Management"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Git <span>Submodules</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Manage complex dependencies by nesting Git repositories. Submodules allow you to keep an external project inside your own while maintaining separate commit histories and version locking.
                    </Typography>
                </Box>

                {/* 1. ADDING SUBMODULES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LibraryAddIcon />} label="1. Incorporating External Repos" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Adding a submodule creates a link in a <code>.gitmodules</code> file. This tracks the URL and the specific path where the external repo lives.
                    </Typography>
                    <CodeBlock
                        title="Add Submodule Command"
                        code={`# Syntax: git submodule add <url> <path>\ngit submodule add https://github.com/example/shared-lib.git libs/shared\n\n# This creates .gitmodules and stages the new directory.`}
                    />
                </Box>

                {/* 2. CLONING & INITIALIZING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SyncIcon />} label="2. Hydrating Submodules" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        When you clone a parent repo, submodules are empty by default. You must initialize and update them to download the actual code.
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <CodeBlock
                                title="Post-Clone Setup"
                                code={`git submodule init\ngit submodule update\n\n# Or in one shot:\ngit submodule update --init --recursive`}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CodeBlock
                                title="Recursive Clone"
                                code={`# Clone parent and all children at once\ngit clone --recurse-submodules <url>`}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* 3. VERSION MANAGEMENT */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<HubIcon />} label="3. Version Locking & Updates" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Parent repos don't track submodule files; they track a <strong>specific commit hash</strong>. If the submodule advances, the parent must explicitly "opt-in" to the new version.
                    </Typography>
                    <CodeBlock
                        title="Updating Submodule Reference"
                        code={`cd libs/shared\ngit pull origin main\n\ncd ../..\n# Git now sees 'libs/shared' as modified (new hash)\ngit add libs/shared\ngit commit -m "chore: bump shared-lib version"`}
                    />
                </Box>

                {/* 4. REMOVAL */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<DeleteSweepIcon />} label="4. Safe Removal Process" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Removing a submodule is a multi-step process involving the Git index, the <code>.gitmodules</code> file, and the internal Git metadata.
                    </Typography>
                    <CodeBlock
                        title="Removal Sequence"
                        code={`# 1. Unregister the submodule\ngit submodule deinit -f libs/shared\n\n# 2. Remove from working tree and .gitmodules\ngit rm -f libs/shared\n\n# 3. Clean up internal git directory\nrm -rf .git/modules/libs/shared`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Submodule Best Practices" />
                    <Grid container spacing={1.5}>
                        {[
                            "Always use 'git clone --recurse-submodules' to avoid empty folders.",
                            "Treat submodules as read-only dependencies when possible.",
                            "Lock submodules to specific tags rather than branches for stability.",
                            "Communicate with your team when bumping submodule versions.",
                            "Avoid deep nesting of submodules to prevent management 'hell'.",
                            "Use submodules for shared internal libraries, not 3rd-party packages (use npm/maven for those)."
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/devops-hub"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to DevOps Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Confused why your submodule is in a "Detached HEAD" state? This is normal! Git checks out the specific commit hash recorded by the parent, not a branch name.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
